"use client";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import PaymentPin from "../../utils/PaymentPin";
import { handleCreatePin } from "../userControllers/pinController";

import authbg from "./assets/authbg.svg";
import load from "./assets/load.gif";
import logo from "./assets/logo.svg";

const CreatePin: React.FC = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [step, setStep] = useState<"create" | "confirm">("create");
    const [initialCode, setInitialCode] = useState<string[]>(new Array(4).fill(""));
    const [confirmCode, setConfirmCode] = useState<string[]>(new Array(4).fill(""));
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("");
    const navigate = useNavigate();

    const handleInitialCodeChange = (newCode: string[]) => {
        setInitialCode([...newCode]);
        setError(""); // Clear error when initial code changes
    };

    const handleConfirmCodeChange = (newCode: string[]) => {
        setConfirmCode([...newCode]);
        // Validate in real-time
        if (
            newCode.join("") !== initialCode.join("") &&
            newCode.every((digit) => digit !== "")
        ) {
            setError("PINs do not match");
        } else {
            setError("");
        }
    };

    const onSuccess = () => {
        setLoading(false);
        navigate("/dashboard");
    };

    const onError = () => {
        setLoading(false);
    };

    const handleNext = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (initialCode.every((digit) => digit !== "")) {
            setStep("confirm");
            setError("");
        } else {
            enqueueSnackbar("Please enter a 4-digit PIN", { variant: "warning" });
        }
    };

    const handleBack = () => {
        setStep("create");
        setConfirmCode(new Array(4).fill(""));
        setError("");
    };

    const handleSend = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        if (initialCode.join("") !== confirmCode.join("")) {
            setError("PINs do not match");
            enqueueSnackbar("PINs do not match", { variant: "error" });
            return;
        }
        setLoading(true);
        const userData = {
            pin: initialCode.join(""),
            pin_confirmation: confirmCode.join(""),
        };
        handleCreatePin(userData, onSuccess, onError);
    };

    // Auth check
    useEffect(() => {
        const storedItem = localStorage.getItem("saca_token");
        if (!storedItem) {
            navigate("/sign-in");
        }
    }, [navigate]);

    return (
        <div
            style={{
                backgroundImage: `url(${authbg})`,
                backgroundPosition: "center",
                backgroundSize: "cover",
            }}
            className="w-full md:w-[120%] h-full bg-[#1212128d] z-[999] fixed top-0 left-0 md:-left-[20%] p-6 flex justify-center items-center"
        >
            <div className="md:ml-[20%] max-h-[550px] bg-[#FFFFFF] p-6 rounded-[15px] w-full md:w-[550px]">
                <div className="w-full h-full bg-[#fff] overflow-y-auto p-4 md:p-4 flex flex-col items-center">
                    <img src={logo} className="w-[20%]" alt="Logo" />
                    <p className="mt-6 text-center font-Manrope font-bold text-[#272D37] text-2xl">
                        {step === "create" ? "Create PIN" : "Confirm PIN"}
                    </p>
                    <p className="text-center max-w-[400px] font-Manrope text-lg mt-3 text-[#272D37]">
                        {step === "create"
                            ? "Enter a 4-digit PIN for your payments."
                            : "Re-enter your PIN to confirm."}
                    </p>

                    <div className="w-full overflow-y-auto max-h-[400px] pr-2 flex flex-col items-center mt-6 font-Manrope">
                        {step === "create" ? (
                            <PaymentPin
                                code={initialCode}
                                onCodeChange={handleInitialCodeChange}
                                forgot={false}
                            />
                        ) : (
                            <>
                                <PaymentPin
                                    code={confirmCode}
                                    onCodeChange={handleConfirmCodeChange}
                                    forgot={false}
                                />
                                {error && (
                                    <p className="text-red-500 text-sm mt-2 font-Manrope">
                                        {error}
                                    </p>
                                )}
                            </>
                        )}
                    </div>

                    <div className="mt-6 w-full grid grid-cols-2 gap-4 md:gap-6">
                        {step === "create" ? (
                            <Link
                                to="/dashboard"
                                className="border border-[#DAE0E6] text-center rounded-md py-3 font-Manrope text-[#272D37] font-semibold text-base"
                            >
                                Skip
                            </Link>
                        ) : (
                            <button
                                onClick={handleBack}
                                className="border border-[#DAE0E6] text-center rounded-md py-3 font-Manrope text-[#272D37] font-semibold text-base"
                            >
                                Back
                            </button>
                        )}
                        <button
                            onClick={step === "create" ? handleNext : handleSend}
                            disabled={loading || (step === "confirm" && !!error)}
                            className={`rounded-md py-3 font-Manrope text-[#fff] font-semibold text-base flex justify-center items-center ${loading || (step === "confirm" && !!error)
                                ? "bg-[#317333]/50 cursor-not-allowed"
                                : "bg-[#317333]"
                                }`}
                        >
                            {loading ? (
                                <img src={load} className="w-5" alt="Loading" />
                            ) : step === "create" ? (
                                "Next"
                            ) : (
                                "Save"
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CreatePin;
